import { useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { LotPickerSource } from '@/components/lots/lot-picker-source'
import { Button } from '@/components/ui/button'
import { LotPicker } from '@/components/lots/lot-picker'
import { QuantityInput } from '@/components/ui/quantity-input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Nhãn hiển thị cho bảng con nguồn — mỗi chặng (phân loại / lấy hương / thành
 * phẩm) truyền bộ nhãn riêng (prefix sl_ / scl_ / pll_).
 */
export interface LotSourceLabels {
    title: string
    empty: string
    add: string
    remove: string
    lotPlaceholder: string
    qtyLabel: string
}

interface LotSourcesSubTableProps {
    idx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    labels: LotSourceLabels
    /** Tên field mảng dòng item, mặc định 'items'. */
    itemsFieldName?: string
    /** Tải thêm khi cuộn tới cuối picker (tuỳ chọn, M-27). Không truyền → như cũ. */
    onLoadMore?: () => void
    hasMore?: boolean
    /** Đang tải trang tiếp — spinner cuối danh sách. */
    loadingMore?: boolean
}

interface SourceSibling {
    source_lot_id?: string
}

/** Một dòng lô nguồn: picker lô + ô kg xuất + nút "lấy hết" + nút xoá. */
function SourceRow({
    itemsFieldName,
    idx,
    sIdx,
    options,
    onSearchChange,
    clientFilter,
    labels,
    onRemove,
    onLoadMore,
    hasMore,
    loadingMore,
}: {
    itemsFieldName: string
    idx: number
    sIdx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    labels: LotSourceLabels
    onRemove: () => void
    onLoadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
}) {
    const base = `${itemsFieldName}.${idx}.sources.${sIdx}`
    const { control, register, setValue, formState } = useFormContext()
    const picked = useWatch({ control, name: `${base}.source_lot_id` }) as
        string | undefined
    // Lô đã chọn ở các dòng nguồn KHÁC của cùng item → loại khỏi options để không
    // chọn trùng (bảng con có UNIQUE(item, lô); trùng sẽ vỡ khi lưu).
    const siblings = useWatch({
        control,
        name: `${itemsFieldName}.${idx}.sources`,
    }) as SourceSibling[] | undefined
    const siblingIds = useMemo(
        () =>
            (siblings ?? [])
                .filter((_, i) => i !== sIdx)
                .map((s) => s?.source_lot_id ?? '')
                .filter(Boolean),
        [siblings, sIdx],
    )
    const errRoot = (
        formState.errors as unknown as Record<
            string,
            Array<{
                sources?: Array<Record<string, { message?: string }>>
            }>
        >
    )[itemsFieldName]?.[idx]?.sources?.[sIdx]
    // Lô đã chọn được cache để nhãn + hint "còn X" không rớt khỏi trigger khi
    // search server đổi options (server chỉ trả 50 dòng theo từ khoá hiện tại).
    // Xem PR #525 / mem:gotchas combobox server-search.
    const [pickedCache, setPickedCache] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const pickedOption =
        options.find((o) => o.value === picked) ??
        (picked ? pickedCache.get(picked) : undefined)
    const remaining = pickedOption?.available
    // Ô chọn lô dùng LotPicker chung; nguồn = options của dòng (server hoặc lô
    // trong phiếu lấy hàng) + lô đã chọn được cache.
    const rowSource: LotPickerSource = {
        options,
        getOption: (id) =>
            id
                ? (options.find((o) => o.value === id) ?? pickedCache.get(id))
                : undefined,
        onSearchChange,
        onLoadMore,
        hasMore,
        loadingMore,
    }

    return (
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div className="min-w-0">
                <LotPicker
                    source={rowSource}
                    clientFilter={!!clientFilter}
                    excludeIds={siblingIds}
                    value={picked ?? ''}
                    onChange={(v, opt) => {
                        if (opt)
                            setPickedCache((prev) => {
                                if (prev.has(v)) return prev
                                const next = new Map(prev)
                                next.set(v, opt)
                                return next
                            })
                        setValue(`${base}.source_lot_id`, v, {
                            shouldValidate: true,
                        })
                    }}
                    placeholder={labels.lotPlaceholder}
                />
                {errRoot?.source_lot_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {errRoot.source_lot_id.message}
                    </p>
                )}
                {remaining != null && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.lot_items_available_hint({
                            qty: formatQty(remaining),
                            // Nguồn chè luôn kèm 'kg' tường minh (purchase/sorting/
                            // scentingLotOption); nguồn thành phẩm dùng product_unit
                            // (optional) → để rỗng, KHÔNG fallback 'kg' kẻo SP hiện sai.
                            unit: pickedOption?.unit ?? '',
                        })}
                    </p>
                )}
            </div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <QuantityInput
                            unit={pickedOption?.unit ?? ''}
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder={labels.qtyLabel}
                            {...register(`${base}.source_quantity`, {
                                setValueAs: (v) =>
                                    v === '' || v == null
                                        ? undefined
                                        : Number(v),
                            })}
                        />
                        {remaining != null && remaining > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                    setValue(
                                        `${base}.source_quantity`,
                                        remaining,
                                        { shouldValidate: true },
                                    )
                                }
                            >
                                {m.lot_items_take_all()}
                            </Button>
                        )}
                    </div>
                    {errRoot?.source_quantity?.message && (
                        <p className="mt-1 text-xs text-destructive">
                            {errRoot.source_quantity.message}
                        </p>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label={labels.remove}
                    onClick={onRemove}
                >
                    <Trash2 className="size-4 text-destructive" />
                </Button>
            </div>
        </div>
    )
}

/**
 * Bảng con NHIỀU lô nguồn của MỘT dòng phiếu nhập (chỉ hiện với lý do đi chuỗi).
 * Dùng chung cho cả 3 chặng — thay cho picker lô nguồn đơn trước đây.
 */
export function LotSourcesSubTable({
    idx,
    options,
    onSearchChange,
    clientFilter,
    labels,
    itemsFieldName = 'items',
    onLoadMore,
    hasMore,
    loadingMore,
}: LotSourcesSubTableProps) {
    const { control, formState } = useFormContext()
    const { fields, append, remove } = useFieldArray({
        control,
        name: `${itemsFieldName}.${idx}.sources`,
    })
    // Lỗi cấp-mảng (superRefine addIssue ở path [items, idx, 'sources']) khi CHƯA có
    // dòng nguồn nào — RHF lưu ở .message hoặc .root.message tuỳ phiên bản resolver.
    const arrErr = (
        formState.errors as unknown as Record<
            string,
            Array<{
                sources?: { message?: string; root?: { message?: string } }
            }>
        >
    )[itemsFieldName]?.[idx]?.sources
    const arrErrMsg =
        fields.length === 0
            ? (arrErr?.message ?? arrErr?.root?.message)
            : undefined

    return (
        <div className="mt-2 rounded-md border border-dashed bg-muted/30 p-2">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                    {labels.title}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        append({
                            source_lot_id: '',
                            source_quantity: undefined,
                        })
                    }
                >
                    <Plus className="size-4 mr-1" />
                    {labels.add}
                </Button>
            </div>
            {fields.length === 0 ? (
                <p
                    className={
                        arrErrMsg
                            ? 'text-xs text-destructive'
                            : 'text-xs text-muted-foreground'
                    }
                >
                    {arrErrMsg ?? labels.empty}
                </p>
            ) : (
                fields.map((f, sIdx) => (
                    <SourceRow
                        key={f.id}
                        itemsFieldName={itemsFieldName}
                        idx={idx}
                        sIdx={sIdx}
                        options={options}
                        onSearchChange={onSearchChange}
                        clientFilter={clientFilter}
                        labels={labels}
                        onRemove={() => remove(sIdx)}
                        onLoadMore={onLoadMore}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                    />
                ))
            )}
        </div>
    )
}
